import { useNavigate } from "react-router-dom";
import type { SuikamonSpecies } from "@shared/domain/petModels";
import {
  getFreePreviewImage,
  getPlayFreePath,
  isPlayableForFree,
} from "../../utils/playFreeLink";
import "./PlayFirstSuikamonBanner.css";
import { EGG_IMAGE_SRC } from "../../utils/petHelpers";
import { useT } from "../../i18n/i18nContext";

interface PlayFirstSuikamonBannerProps {
  /**
   * Species landing pages (`/bireon`) pass their species so the banner names it and drops the
   * visitor into the picker with that Suikamon already selected. Ignored for species that
   * aren't free starters — those keep the generic "pick a Suikamon" copy.
   */
  species?: SuikamonSpecies | null;
  /** Override the CTA action. Defaults to the free starter flow (`/play-free`). */
  onPlay?: () => void;
}

/**
 * Ad-style sticky prompt shown app-wide to visitors who own no Suikamon yet — the
 * always-visible, one-tap entry into the game. It says **Play**, never "Hatch": hatching is a
 * later in-game step (warming an egg you already own), so an onboarding CTA that says "hatch"
 * reads as a different, unrelated action to a first-time visitor.
 *
 * Two shapes (CSS only, same markup): a full-width bar above the bottom nav on phones, and a
 * compact bottom-RIGHT corner card on desktop, where a full-width strip would be too heavy.
 *
 * Rendered once in App (above the routes) and paired with {@link PlayFirstSuikamonSpacer} so
 * the fixed banner never covers page content.
 */
export function PlayFirstSuikamonBanner({ species, onPlay }: PlayFirstSuikamonBannerProps) {
  const t = useT();
  const navigate = useNavigate();
  const target = species && isPlayableForFree(species) ? species : null;
  const handlePlay = () => (onPlay ? onPlay() : navigate(getPlayFreePath(target?.id)));

  const title = target ? t("home.banner.titleNamed", { species: target.name }) : t("home.startPlaying.title");
  const subtitle = target
    ? t("home.banner.subtitleNamed", { species: target.name })
    : t("home.banner.subtitle");
  const artwork = target ? getFreePreviewImage(target) : EGG_IMAGE_SRC;

  return (
    <div
      className="play-first-banner"
      role="button"
      tabIndex={0}
      aria-label={title}
      onClick={handlePlay}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handlePlay();
        }
      }}
    >
      <img
        src={artwork}
        alt=""
        aria-hidden="true"
        className="play-first-banner__art"
        draggable={false}
      />
      <div className="play-first-banner__copy">
        <span className="play-first-banner__title">{title}</span>
        <span className="play-first-banner__subtitle">{subtitle}</span>
      </div>
      <button
        type="button"
        className="play-first-banner__cta"
        onClick={(e) => {
          e.stopPropagation();
          handlePlay();
        }}
      >
        {t("home.banner.cta")}
      </button>
    </div>
  );
}

/**
 * Flow-level spacer that reserves the sticky banner's height at the bottom of the page.
 * Render it next to the routed content whenever {@link PlayFirstSuikamonBanner} is mounted.
 */
export function PlayFirstSuikamonSpacer() {
  return <div className="play-first-banner-spacer" aria-hidden="true" />;
}
